import { useEffect, useState } from 'react';
import { useFormState, useForm } from 'react-hook-form';

import { l10n } from '@ott/l10n';
import { hooks as virtualCardHooks } from '@ott/shared-virtual-card-logic';

import { yupResolver } from '@hookform/resolvers/yup';

import { ReleaseErrors } from '../../../helpers/constants';
import { FORM_FIELDS, FormValues } from '../helpers/constants';
import { formatErrorsToRedux } from '../helpers/formatErrorsToRedux';
import { getValidationSchema } from '../helpers/getValidationSchema';

type Props = {
  isPinSent: boolean;
  onFormSubmit: (values: FormValues) => Promise<void>;
  serverError: string | Record<string, any> | undefined;
};

const {
  virtualCardFlow: { useVirtualCardFlow },
} = virtualCardHooks;

const useReleaseForm = (props: Props) => {
  const { onFormSubmit, isPinSent, serverError } = props;

  const [time, setTime] = useState(Date.now());

  const {
    auth: { phone: authPhone, isAuthorized },
  } = useVirtualCardFlow();

  const phone = isAuthorized ? authPhone : '79';

  const { handleSubmit, control, reset, getValues, setError, setFocus } = useForm<FormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(getValidationSchema({ isPinSent })) as any,
    defaultValues: {
      [FORM_FIELDS.PHONE]: phone,
      [FORM_FIELDS.PIN]: '',
    },
  });

  const { isSubmitted, errors } = useFormState({
    control,
  });

  useEffect(() => {
    if (isSubmitted) {
      setTime(Date.now());
    }
  }, [isSubmitted]);

  const handleFormSubmit = async (values: FormValues) => {
    await onFormSubmit(values);
  };

  const handleInvalidPin = () => {
    if (!serverError) {
      return;
    }

    if (typeof serverError === 'string' && serverError === ReleaseErrors.invalidPin) {
      setError(FORM_FIELDS.PIN, { message: l10n('virtualCard.releaseForm.errors.incorrectPin') });
      setFocus(FORM_FIELDS.PIN);
    }
  };

  useEffect(() => {
    handleInvalidPin();
  }, [serverError]);

  return {
    time,
    control,
    isSubmitted,
    errors: formatErrorsToRedux(errors),
    onFormSubmit: handleSubmit(handleFormSubmit),
    reset,
    getValues,
    handleInvalidPin,
  };
};

export default useReleaseForm;
